import { useCallback, useState } from 'react';
import { useAsyncFn, useInterval } from 'react-use';

const POLLING_INTERVAL = 5000;

interface PollConfig<T> {
  poll: () => Promise<T>;
  onPoll: (data: T) => boolean | null;
  interval?: number;
}

export const usePoll = <T>({
  poll,
  onPoll,
  interval = POLLING_INTERVAL,
}: PollConfig<T>) => {
  const [polling, setPolling] = useState<number | null>(null);

  const [{ error }, getStatus] = useAsyncFn(async () => {
    const data = await poll();
    const status = onPoll(data);
    if (status === null) return;
    if (status) {
      setPolling(null);
    } else {
      setPolling(null);
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw {
        errorCode: 'POLL_FAILURE',
        reason: 'Unexpected error occurred while polling.',
      };
    }
  }, [poll, onPoll, setPolling]);

  const startPolling = useCallback(() => {
    // we don't want to wait for the first interval
    void getStatus();
    setPolling(interval);
  }, [setPolling, interval, getStatus]);

  useInterval(() => {
    void getStatus();
  }, polling);

  return {
    start: startPolling,
    error,
    isPolling: polling !== null,
  };
};
